import { useContext, useEffect } from "react";
import { Box, Icon, Typography } from "@mui/material";
import { AuthContext } from "src/utils/AuthContext.js";
import { customEvent } from "src/utils/gtag.js";
import { hasValidConsumerSubscription } from "src/services";
import { UpgradeConsumerButton } from "src/components/upgrades/consumer/UpgradeConsumerButton.js";
import { FREE_CALENDAR_LIMIT } from "./stanzaPlusProps.js";

const SOURCE = "sidebar";

/**
 * Minimal Stanza+ upsell in the sidebar for signed-in free users. Shows their
 * progress toward the free calendar limit (or an at-limit prompt once reached).
 * Members and signed-out visitors render nothing. The standard
 * `UpgradeConsumerButton` owns the CTA and its tracking.
 */
const SidebarUpgradeCard = () => {
  const { user } = useContext(AuthContext) ?? {};
  const followingCount = user?.consumerSlotsUsed ?? user?.addedCalendars?.length ?? 0;
  const filled = Math.min(followingCount, FREE_CALENDAR_LIMIT);
  const atLimit = followingCount >= FREE_CALENDAR_LIMIT;
  const eligible = Boolean(user) && !hasValidConsumerSubscription(user);

  useEffect(() => {
    if (!eligible) return;
    customEvent({
      name: "stanza_plus_upsell_impression",
      category: "stanza_plus_upsell",
      label: SOURCE,
      data: { user_id: user._id, user_email: user.email },
    });
    // Fire once per eligible mount; `user` re-renders must not re-fire it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligible]);

  if (!eligible) return null;

  return (
    <Box
      data-testid="sidebar-upgrade-card"
      sx={{
        mt: 1,
        mx: 1,
        p: 2,
        borderRadius: "12px",
        border: (theme) => `1.5px solid ${theme.palette.text.primary}`,
        // Stay invisible while the sidebar column eases open, then fade in once
        // it has settled, so the card never squeezes/reflows on open. Motion-safe
        // users get the fade; reduced-motion users just see it immediately.
        "@media (prefers-reduced-motion: no-preference)": {
          opacity: 0,
          animation: "sidebarUpsellFadeIn 200ms ease 320ms forwards",
        },
        "@keyframes sidebarUpsellFadeIn": { to: { opacity: 1 } },
      }}
    >
      <Typography sx={{ fontSize: "12px", fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "text.secondary" }}>
        Stanza+
      </Typography>
      <Typography sx={{ fontSize: "15px", fontWeight: 800, mt: 0.25 }}>
        {atLimit ? "You've hit the free limit" : "Go unlimited"}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 1, mb: 1 }}>
        {Array.from({ length: FREE_CALENDAR_LIMIT }).map((_, i) => (
          <Box
            key={i}
            sx={{
              flex: 1,
              height: 8,
              borderRadius: 999,
              backgroundColor: i < filled ? "text.primary" : "overlay.black10",
            }}
          />
        ))}
        <Typography sx={{ fontSize: "12px", fontWeight: 800, color: "text.secondary", ml: 0.25, whiteSpace: "nowrap" }}>
          {filled} / {FREE_CALENDAR_LIMIT} free
        </Typography>
      </Box>
      <Typography sx={{ fontSize: "13px", color: "text.secondary", mb: 1.5 }}>
        Plus reminders and personalization.
      </Typography>
      <UpgradeConsumerButton
        state={{ source: `upsell_${SOURCE}` }}
        variant="text"
        endIcon={<Icon baseClassName="material-symbols-outlined" sx={{ fontSize: "1.1rem" }}>arrow_forward</Icon>}
        sx={{ width: "auto", minWidth: 0, px: 0, py: 0, justifyContent: "flex-start", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 700, fontSize: "14px", "&:hover": { backgroundColor: "transparent" } }}
      >
        Start free trial
      </UpgradeConsumerButton>
    </Box>
  );
};

export default SidebarUpgradeCard;
